/**
 * Process-scoped cache for parsed 3D models (GLB/GLTF/OBJ).
 *
 * Loading a model is dominated by the PARSE (geometry construction, texture
 * decode, draco/accessor work) — not the network — so two views that show the
 * same model (the editing `UploadedModel` and the measurement `MeasurementColoredModel`)
 * would each pay that multi-second cost independently. This cache parses a given
 * URL exactly once and hands out cheap clones: whichever view mounts first warms
 * the cache, and the other just clones (a typed-array copy, no re-parse), which
 * is why switching into measurement mode no longer blocks for seconds.
 *
 * The cached object is treated as an immutable template — callers always receive
 * a clone (see `cloneParsedModel`) so their in-place edits (Z-up `applyMatrix4`,
 * material swaps, edge lines, userData) never corrupt the template or each other.
 */
import * as THREE from 'three';
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

interface ParsedModel {
  /** Parsed scene/group. Never mutate — clone via `cloneParsedModel` before use. */
  object: THREE.Object3D;
  /** node-index → original name (GLTF parser `nodes`, or OBJ child index). */
  originalNames: Map<number, string>;
}

/** Drop the query string so different presigned signatures for the same file share a cache entry. */
const cacheKey = (url: string): string => url.split('?')[0];

const cache = new Map<string, Promise<ParsedModel>>();

const parseModel = async (url: string, extension: string): Promise<ParsedModel> => {
  const originalNames = new Map<number, string>();
  let object: THREE.Object3D;

  if (extension === 'glb' || extension === 'gltf') {
    const result = (await new GLTFLoader().loadAsync(url)) as GLTF;
    object = result.scene;
    if (result.parser.json.nodes) {
      result.parser.json.nodes.forEach((node: { name?: string }, index: number) => {
        if (node.name) originalNames.set(index, node.name);
      });
    }
  } else if (extension === 'obj') {
    object = (await new OBJLoader().loadAsync(url)) as THREE.Group;
    object.children.forEach((child: THREE.Object3D, index: number) => {
      originalNames.set(index, child.name || `Object_${index}`);
    });
  } else {
    throw new Error(`Unsupported format: ${extension}`);
  }

  return { object, originalNames };
};

/** Parse a model once per URL (cached). Callers must clone the result before mutating it. */
export function loadParsedModel(url: string, extension: string): Promise<ParsedModel> {
  const key = cacheKey(url);
  let entry = cache.get(key);
  if (!entry) {
    entry = parseModel(url, extension).catch((err) => {
      cache.delete(key); // allow a later retry instead of caching the failure
      throw err;
    });
    cache.set(key, entry);
  }
  return entry;
}

/**
 * Deep-clone a cached model for exclusive use by one consumer. `Object3D.clone`
 * shares geometry by reference, but callers transform geometry in place (Z-up
 * matrix), so each mesh gets its own geometry copy. Materials are intentionally
 * shared — consumers reassign `child.material` rather than mutating it, and the
 * shared textures stay decoded/uploaded only once.
 */
export function cloneParsedModel(parsed: ParsedModel): ParsedModel {
  const object = parsed.object.clone(true);
  object.traverse((child: THREE.Object3D) => {
    if (child instanceof THREE.Mesh) {
      child.geometry = child.geometry.clone();
    }
  });
  return { object, originalNames: parsed.originalNames };
}
